import { memo, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { useThemeStore } from '@/store/themeStore';

import { ThemeSwitch } from '@/components/shared/ThemeSwitch';
import { CSS_COLORS } from '@/constants/pageStyles';
import { PATHS } from '@/constants/path';
import { PERMISSIONS } from '@/constants/permissions';
import { TYPOGRAPHY as SHARED_TYPOGRAPHY } from '@/constants/styles';
import { usePermission } from '@/hooks/usePermission';
import { useThemePreference } from '@/hooks/useThemePreference';
import { cn } from '@/lib/utils';

const SIDEBAR = {
  expandedWidth: 216,
  collapsedWidth: 64,
  bgColor: CSS_COLORS.card,
  borderColor: CSS_COLORS.border,
  activeBg: CSS_COLORS.primary,
  iconSize: 16,
  logoExpandedWidth: 53,
  logoExpandedHeight: 40,
  logoCollapsedSize: 40,
  logoSectionHeight: 48,
  menuItemWidth: 184,
  menuItemHeight: 32,
  menuItemGap: 12,
} as const;

const TYPOGRAPHY = {
  menuItem: {
    fontFamily: SHARED_TYPOGRAPHY.fontFamily,
    fontWeight: SHARED_TYPOGRAPHY.fontWeight.semibold,
    fontSize: SHARED_TYPOGRAPHY.fontSize.xs,
  },
} as const;

interface SidebarItem {
  id: string;
  icon: string;
  label: string;
  href: string;
  permissions?: string[];
}

const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    id: 'users',
    icon: 'group',
    label: 'header.sidebar.userManagement',
    href: PATHS.ADMIN.USERS.LIST,
    permissions: [PERMISSIONS.USER.SEARCH],
  },
  {
    id: 'reportFilters',
    icon: 'person_shield',
    label: 'reportFilters.title',
    href: PATHS.ADMIN.REPORT_FILTERS.LIST,
    permissions: [PERMISSIONS.REPORT_FILTER.SEARCH],
  },
  {
    id: 'roles',
    icon: 'passkey',
    label: 'header.sidebar.roleManagement',
    href: PATHS.ADMIN.ROLES.LIST,
    permissions: [PERMISSIONS.ROLE.SEARCH],
  },
  {
    id: 'apps',
    icon: 'grid_view',
    label: 'header.sidebar.appManagement',
    href: PATHS.ADMIN.APPS.LIST,
    permissions: [PERMISSIONS.APPLICATION.SEARCH],
  },
  {
    id: 'reports',
    icon: 'assignment',
    label: 'header.sidebar.reportManagement',
    href: PATHS.ADMIN.REPORTS.LIST,
    permissions: [PERMISSIONS.REPORT.SEARCH],
  },
  {
    id: 'organization',
    icon: 'apartment',
    label: 'header.sidebar.organization',
    href: PATHS.ADMIN.ORGANIZATION.LIST,
    permissions: [PERMISSIONS.COMPANY.SEARCH, PERMISSIONS.DEPARTMENT.SEARCH],
  },
  {
    id: 'chatbots',
    icon: 'smart_toy',
    label: 'chatbots.title',
    href: PATHS.ADMIN.CHATBOTS.LIST,
    permissions: [PERMISSIONS.CHATBOT.SEARCH],
  },
  {
    id: 'settings',
    icon: 'settings',
    label: 'header.sidebar.systemConfig',
    href: PATHS.ADMIN.SETTINGS.LIST,
    permissions: [PERMISSIONS.THEME.UPDATE],
  },
  {
    id: 'activity',
    icon: 'nest_clock_farsight_analog',
    label: 'header.sidebar.activityLog',
    href: PATHS.ADMIN.ACTIVITY.LIST,
    permissions: [PERMISSIONS.LOG.SEARCH],
  },
];

const ICON_STYLE = { fontSize: `${SIDEBAR.iconSize}px` } as const;

interface SidebarMenuItemProps {
  item: SidebarItem;
  isActive: boolean;
  isExpanded: boolean;
}

const SidebarMenuItem = memo(function SidebarMenuItem({
  item,
  isActive,
  isExpanded,
}: SidebarMenuItemProps) {
  const { t } = useTranslation();
  const translatedLabel = t(item.label);

  const containerStyle = useMemo(
    () => ({
      width: isExpanded
        ? `${SIDEBAR.menuItemWidth}px`
        : `${SIDEBAR.logoExpandedHeight}px`,
      height: `${SIDEBAR.menuItemHeight}px`,
      ...(isActive && { backgroundColor: SIDEBAR.activeBg }),
      borderRadius: '8px',
      ...TYPOGRAPHY.menuItem,
    }),
    [isActive, isExpanded],
  );

  return (
    <Link
      to={item.href}
      className={cn(
        'group relative overflow-hidden transition-all duration-300 ease-in-out',
        !isActive && 'hover:bg-primary/10 hover:shadow-sm',
        isActive ? 'pointer-events-none' : '',
        isExpanded
          ? 'flex-start gap-3 px-3'
          : 'flex items-center justify-center',
      )}
      style={containerStyle}
      title={!isExpanded ? translatedLabel : undefined}
    >
      <span
        className={cn(
          'material-symbols-rounded shrink-0 transition-colors duration-200',
          isActive ? 'text-white' : 'text-muted-foreground',
        )}
        style={ICON_STYLE}
      >
        {item.icon}
      </span>

      <span
        className={cn(
          'overflow-hidden whitespace-nowrap',
          isActive ? 'text-white' : 'text-foreground',
          isExpanded
            ? 'max-w-50 opacity-100 delay-100 duration-300'
            : 'max-w-0 opacity-0 delay-0 duration-150',
        )}
        style={{
          ...TYPOGRAPHY.menuItem,
          transition: 'max-width, opacity',
          transitionTimingFunction: 'ease-in-out',
        }}
      >
        {translatedLabel}
      </span>
    </Link>
  );
});

const TOGGLE_ICON_STYLE = {
  fontSize: '17px',
  color: 'var(--custom-neutral-300)',
} as const;

interface AdminSidebarProps {
  className?: string;
  isExpanded?: boolean;
  onToggle?: () => void;
}

export const AdminSidebar = memo(function AdminSidebar({
  className,
  isExpanded = true,
  onToggle,
}: AdminSidebarProps) {
  const location = useLocation();
  const { can } = usePermission();
  const { updateTheme, currentTheme } = useThemePreference();
  const { theme: configTheme } = useThemeStore();

  const isLightMode = currentTheme !== 'dark';

  const visibleItems = useMemo(
    () =>
      SIDEBAR_ITEMS.filter(item => {
        if (!item.permissions) return true;
        return can(item.permissions);
      }),
    [can],
  );

  const isItemActive = useCallback(
    (href: string) => {
      if (href === PATHS.ROOT) return location.pathname === PATHS.ROOT;
      return location.pathname.startsWith(href);
    },
    [location.pathname],
  );

  const handleThemeToggle = useCallback(() => {
    updateTheme(isLightMode ? 'dark' : 'light');
  }, [isLightMode, updateTheme]);

  const sidebarStyle = useMemo(
    () => ({
      width: `${isExpanded ? SIDEBAR.expandedWidth : SIDEBAR.collapsedWidth}px`,
      backgroundColor: SIDEBAR.bgColor,
      borderRight: `1px solid ${SIDEBAR.borderColor}`,
    }),
    [isExpanded],
  );

  const logoSectionStyle = useMemo(
    () => ({
      height: `${SIDEBAR.logoSectionHeight}px`,
      borderBottom: `1px solid ${SIDEBAR.borderColor}`,
    }),
    [],
  );

  const logoStyle = useMemo(
    () => ({
      width: isExpanded
        ? `${SIDEBAR.logoExpandedWidth}px`
        : `${SIDEBAR.logoCollapsedSize}px`,
      height: isExpanded
        ? `${SIDEBAR.logoExpandedHeight}px`
        : `${SIDEBAR.logoCollapsedSize}px`,
    }),
    [isExpanded],
  );

  return (
    <aside
      className={cn(
        'flex h-full flex-col transition-[width] duration-300 ease-in-out',
        className,
      )}
      style={sidebarStyle}
    >
      <div
        className="flex shrink-0 items-center"
        style={{
          ...logoSectionStyle,
          padding: isExpanded ? '0 12px 0 16px' : '0',
          justifyContent: isExpanded ? 'space-between' : 'center',
        }}
      >
        {isExpanded ? (
          <>
            <div className="flex items-center">
              <img
                src={configTheme?.images.logoMenu}
                alt="Gas South"
                style={logoStyle}
                className="object-contain"
              />
            </div>
            <button
              type="button"
              onClick={onToggle}
              className="hover:bg-primary/10 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded transition-colors"
              title="Thu gọn sidebar"
            >
              <span
                className="material-symbols-rounded"
                style={TOGGLE_ICON_STYLE}
              >
                left_panel_close
              </span>
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className="flex cursor-pointer items-center justify-center"
            title="Mở rộng sidebar"
          >
            <img
              src={configTheme?.images.logoMini}
              alt="Gas South"
              style={logoStyle}
              className="object-contain"
            />
          </button>
        )}
      </div>

      <nav
        className={cn(
          'flex flex-1 flex-col items-center py-3',
          isExpanded ? 'px-3' : 'px-2',
        )}
        style={{ gap: `${SIDEBAR.menuItemGap}px` }}
      >
        {visibleItems.map(item => (
          <SidebarMenuItem
            key={item.id}
            item={item}
            isActive={isItemActive(item.href)}
            isExpanded={isExpanded}
          />
        ))}
      </nav>

      <div
        className={cn(
          'flex items-center',
          isExpanded ? 'px-4' : 'justify-center px-2',
        )}
        style={logoSectionStyle}
      >
        <ThemeSwitch
          isLightMode={isLightMode}
          onToggle={handleThemeToggle}
          isExpanded={isExpanded}
        />
      </div>
    </aside>
  );
});

export default AdminSidebar;
